import { Dispatch, SetStateAction, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ApHubTypeEnum, Purchaser } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useGetAiScanningEmailAddresses from '@/modules/admin-center/hooks/useGetAiScanningEmailAddresses'
import useGetLazyOrganisation from '@/modules/admin-center/hooks/useGetLazyOrganisation'
import { Accordion } from '@/modules/shared/components/accordion/Accordion'
import Button from '@/modules/shared/components/button/Button'
import { Modal } from '@/modules/shared/components/modal/ModalComponent'
import { TableRowSingle } from '@/modules/shared/components/page-header-details-table/TableRowSingle'
import { QueryResult } from '@/modules/shared/components/query-result/QueryResult'
import { BooleanItem } from '@/modules/shared/components/table/table-column-items/shared/BooleanItem'

interface OrganisationHierarchyInfoModalProps {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  organisationId: number
}

export function OrganisationHierarchyInfoModal({
  showModal,
  setShowModal,
  organisationId,
}: OrganisationHierarchyInfoModalProps) {
  const { t } = useTranslation()
  const { fetchOrganisation, organisation, loadingStates, error } = useGetLazyOrganisation({
    organisationId,
    fetchPolicy: 'no-cache',
  })
  const { fetchAiScanningEmailAddresses, emailAddresses } = useGetAiScanningEmailAddresses({
    lazyQuery: true,
    ownerIdProp: Number(organisationId),
  })
  const {
    id,
    name,
    legalEntity,
    departmentCode,
    paperlessInvoicingIdentityEmail,
    invoiceWorkflow,
    featureFlags,
    isVerified,
  } = (organisation as Purchaser) || {}

  const onCloseModal = () => {
    setShowModal(false)
  }

  useEffect(() => {
    if (showModal) {
      fetchOrganisation()
      fetchAiScanningEmailAddresses()
    }
  }, [showModal])

  return (
    <>
      <Modal showModal={showModal} onCloseModal={onCloseModal}>
        <Modal.Panel className="flex max-h-[80vh] w-full flex-col overflow-hidden rounded-md bg-white text-sm shadow-xl transition-all md:max-w-[56.25rem]">
          <Modal.Title
            title={t('general.organisationUnitInformation', 'Organisation Unit Information')}
            onCloseModal={onCloseModal}
          />
          <Modal.Body>
            <QueryResult loading={loadingStates.loading} error={error}>
              <Accordion
                title={t('general.organisationDetails', 'Organisation Details')}
                buttonClassName="px-3 h-11 my-2.5"
                titleClassName="font-bold"
                defaultOpen
              >
                <div className="w-full divide-y divide-gray-200 overflow-auto rounded-md border border-gray-300">
                  <TableRowSingle
                    titleText={t('general.organisationUnit', 'Organisation Unit')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.organisationUnitTooltip',
                      'The name of the Organisation Unit.'
                    )}
                    testId="organisationUnit"
                    value={name}
                  />
                  <TableRowSingle
                    titleText={t('general.organisationUnitId', 'Organisation Unit ID')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.organisationUnitIdTooltip',
                      'The unique identifier for the Organisation Unit in PurchasePlus.'
                    )}
                    testId="organisationUnitId"
                    value={id}
                  />
                  <TableRowSingle
                    titleText={t('general.legalEntity', 'Legal Entity')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.legalEntityTooltip',
                      'Indicates if the Organisation is a Legal Entity or not. In PurchasePlus, a Legal Entity is treated differently to a Non-Legal Entity.'
                    )}
                    testId="legalEntity"
                    value={<BooleanItem value={!!legalEntity} />}
                  />
                  <TableRowSingle
                    titleText={t('general.isVerified', 'Is Verified')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.isVerifiedTooltip',
                      'Indicates that the Organisation is a formal user of PurchasePlus.'
                    )}
                    testId="isVerified"
                    value={<BooleanItem value={!!isVerified} />}
                  />
                  <TableRowSingle
                    titleText={t('general.costCenterCode', 'Cost Center Code')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.costCenterCodeTooltip',
                      'The Cost Center Code that applies to the Organisation Unit. This can be useful to help identify expenses that apply to this Organisation.'
                    )}
                    testId="costCentreCode"
                    value={departmentCode}
                  />
                  <TableRowSingle
                    titleText={t('general.defaultAccount', 'Default Account')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.defaultAccountCodeTooltip',
                      'The Account that will be selected by default when a Document is created against this Organisation Unit.'
                    )}
                    testId="defaultAccountCode"
                    value="--"
                  />
                  <TableRowSingle
                    titleText={t('general.paperlessEmailAddress', 'Paperless Email Address')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.paperlessEmailAddressTooltip',
                      'The email address that Suppliers can use to send PDF Invoices to this Organisation. The PDF document will be checked, scanned and created in PurchasePlus as a new Invoice from the Supplier.'
                    )}
                    testId="paperlessEmailAddress"
                    value={paperlessInvoicingIdentityEmail}
                  />
                  <TableRowSingle
                    titleText={t('general.aiScanningEmailAddresses', 'AI Scanning Email Addresses')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.aiScanningEmailAddressesTooltip',
                      'The AI Scanning Email Addresses that apply to this Organisation. If the Organisation has the AI Scanning feature enabled. Invoice and Credit Note PDF attachments sent to these email addresses will be captured and processed as Scanning Tasks.'
                    )}
                    testId="aiScanningEmailAddresses"
                    value={emailAddresses.map((email) => email.email).join(', ')}
                  />
                  <TableRowSingle
                    titleText={t('general.invoiceApprovalWorkflow', 'Invoice Approval Workflow')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.invoiceApprovalWorkflowTooltip',
                      'The Approval Workflow that Invoices raised against this Organisation Unit will be placed into.'
                    )}
                    testId="invoiceApprovalWorkflow"
                    value={invoiceWorkflow?.name}
                  />
                  <TableRowSingle
                    titleText={t('general.apSystemType', 'AP System Type')}
                    tooltipText={t(
                      'organisationHierarchy.organisationDetails.apSystemTypeTooltip',
                      'The AP System type that users at this Organisation will experience. Type A should be used if the Organisation exports Invoices and Credit Notes one at a time, to a preset electronic destination. Type B should be used if the Organisation manually creates Export Files via the Export Center, and downloads them or sends them to an electronic destination at their discretion.'
                    )}
                    testId="apSystemType"
                    value={
                      featureFlags?.apHubType === ApHubTypeEnum.TypeAAutomated
                        ? t('general.typeA', 'Type A')
                        : t('general.typeB', 'Type B')
                    }
                  />
                </div>
              </Accordion>
            </QueryResult>
          </Modal.Body>
          <Modal.Footer className="flex flex-col-reverse items-center justify-end gap-2 md:flex-row">
            <Button
              type="button"
              className="h-11 w-full rounded-md bg-gray-200 px-5 text-sm md:w-fit"
              onClick={onCloseModal}
            >
              {t('general.close', 'Close')}
            </Button>
          </Modal.Footer>
        </Modal.Panel>
      </Modal>
    </>
  )
}
